import classNames from "classnames";
import { FieldMessage, fieldDescribedBy, helpTextId } from "./FieldValue";
import { SpacingFieldProps } from "./SpacingField.types";
import { SideNames, Sides, formatSpacing, parseSpacing } from "./spacing";

const Step =
  "btn btn-sm shadow-none p-0 w-32 h-32 border-0 rounded-1 bg-transparent text-body-secondary hover-bg-light d-flex align-items-center justify-content-center flex-shrink-0";

function Stepper({
  label,
  side,
  value,
  disabled,
  onChange,
}: {
  label: string;
  side: string;
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  const set = (next: number) => onChange(Math.max(0, next));

  return (
    <div className="d-flex align-items-center gap-1 bg-white border rounded-2 p-1" role="group" aria-label={`${label} ${side}`}>
      <button
        type="button"
        className={Step}
        aria-label={`Decrease ${label} ${side}`}
        disabled={disabled || value <= 0}
        onClick={() => set(value - 1)}
      >
        &minus;
      </button>
      <input
        type="text"
        inputMode="numeric"
        className="form-control form-control-sm text-center px-1 w-40 h-32 shadow-none"
        aria-label={`${label} ${side} in pixels`}
        value={value}
        disabled={disabled}
        onChange={(event) => set(parseInt(event.target.value.replace(/\D/g, ""), 10) || 0)}
      />
      <button
        type="button"
        className={Step}
        aria-label={`Increase ${label} ${side}`}
        disabled={disabled}
        onClick={() => set(value + 1)}
      >
        +
      </button>
    </div>
  );
}

/**
 * Space round something, as its top, right, bottom and left in pixels, drawn round a box
 * the way it falls. Stored as CSS shorthand, so one to four values read in are spread to
 * the four sides and written back as short as they can be.
 */
export function SpacingField({ label, name, value, onChange, disabled, helpText, error, className = "" }: SpacingFieldProps) {
  const sides: Sides = parseSpacing(value) ?? [0, 0, 0, 0];
  const stepper = (index: number) => (
    <Stepper
      label={label}
      side={SideNames[index]}
      value={sides[index]}
      disabled={disabled}
      onChange={(next) => onChange(formatSpacing(sides.map((side, at) => (at === index ? next : side)) as Sides))}
    />
  );

  return (
    <fieldset
      className={classNames("field-value", className)}
      aria-describedby={fieldDescribedBy(name, helpText, error)}
      aria-invalid={error ? true : undefined}
    >
      <legend className="field-value__label form-label">
        {label} <span className="fw-normal text-body-secondary">(px)</span>
      </legend>
      <div className="d-inline-flex flex-column align-items-center gap-1">
        {stepper(0)}
        <div className="d-flex align-items-center gap-1">
          {stepper(3)}
          <span className="border border-dashed rounded-1 bg-light flex-shrink-0 w-40 h-40" aria-hidden="true" />
          {stepper(1)}
        </div>
        {stepper(2)}
      </div>
      {helpText && (
        <p id={helpTextId(name)} className="form-text small text-body-secondary mb-0">
          {helpText}
        </p>
      )}
      <FieldMessage name={name} error={error} />
    </fieldset>
  );
}
